import { useEffect, ComponentType, ReactNode } from 'react'
import styled from 'styled-components'
import { useTrackVisibility } from 'react-intersection-observer-hook'

import { HeaderPlaceholder } from '../../cards/shared/styled'
import PlaceholderCard from '../../cards/PlaceholderCard'

const Wrapper = styled.div`
  margin: 0 -16px;

  ${HeaderPlaceholder} {
    display: block !important;
  }
`

const PlaceholderCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

export const HorizontalListWrapper = styled.div`
  transform: translate3d(0, 0, 0);
  margin: 0 -8px;
  overflow: hidden;
`

export const HorizontalListTrack = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: hidden;
`

export const HorizontalListItem = styled.div<{ $empty?: boolean }>`
  display: inline-block;
  vertical-align: top;
  width: ${({ $empty }) => !$empty && '320px'};
  margin: 0 8px;
  flex: none;

  &:first-child {
    margin-left: 24px;
  }
`

export const HorizontalList = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: auto;
  white-space: nowrap;
  overflow-x: scroll;
  -webkit-overflow-scrolling: touch;

  padding-bottom: 48px;
  margin-bottom: -30px;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`

type ListProps<T> = {
  items: T[]
  ItemComponent: ComponentType<{ item: T }>
  loading: boolean
  canFetchMore: boolean
  onFetchMore: (...args: any) => any
  loadingPlaceholder: ReactNode
}

const List = <T extends Record<'id', string>>({
  loading,
  canFetchMore,
  onFetchMore,
  items,
  ItemComponent,
  loadingPlaceholder = <PlaceholderCard portrait={false} />,
}: ListProps<T>) => {
  const [loaderRef, { isVisible }] = useTrackVisibility()

  useEffect(() => {
    if (isVisible && canFetchMore && !loading) {
      onFetchMore()
    }
  }, [isVisible, canFetchMore, loading])

  return (
    <Wrapper>
      <HorizontalListWrapper>
        <HorizontalListTrack>
          <HorizontalList>
            {items.map((item) => (
              <HorizontalListItem key={item.id}>
                <ItemComponent item={item} />
              </HorizontalListItem>
            ))}

            <HorizontalListItem ref={loaderRef} $empty={!canFetchMore}>
              {canFetchMore && (
                <PlaceholderCardWrapper>
                  {loadingPlaceholder}
                </PlaceholderCardWrapper>
              )}
            </HorizontalListItem>
          </HorizontalList>
        </HorizontalListTrack>
      </HorizontalListWrapper>
    </Wrapper>
  )
}

export default List
